import React, { useState, useEffect } from 'react';
import './HangmanGame.css';

/**
 * MCQ Hangman Game - Answer questions to save yourself!
 * Features:
 * - Multiple choice questions
 * - Realistic hangman graphics
 * - Reverse mechanics: correct answer = step back, wrong = step forward
 * - Subject filtering
 * - Score tracking
 */
const HangmanGame = ({ data }) => {
  const [gameState, setGameState] = useState('menu'); // menu, playing, won, lost
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [hangmanStage, setHangmanStage] = useState(0); // 0 = safe, 7 = dead
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [questions, setQuestions] = useState([]);
  const [usedQuestions, setUsedQuestions] = useState(new Set());
  const [feedback, setFeedback] = useState(null);

  const MAX_STAGE = 7; // 7 stages until death
  const WIN_QUESTIONS = 10; // Answer 10 questions to win

  useEffect(() => {
    if (data && data.subjects) {
      loadQuestions();
    }
  }, [data, selectedSubject]);

  const loadQuestions = () => {
    let allQuestions = [];
    
    data.subjects.forEach(subject => {
      if (selectedSubject !== 'all' && subject.name !== selectedSubject) return;
      
      subject.topics.forEach(topic => {
        if (topic.multiple_choice_questions) {
          topic.multiple_choice_questions.forEach(q => {
            allQuestions.push({
              ...q,
              subject: subject.name,
              topic: topic.name
            });
          });
        }
      });
    });

    setQuestions(allQuestions);
  };

  const startGame = () => {
    if (questions.length === 0) return;
    
    setHangmanStage(3); // Start at middle stage
    setQuestionsAnswered(0);
    setUsedQuestions(new Set());
    setFeedback(null);
    setGameState('playing');
    loadNextQuestion();
  };

  const loadNextQuestion = () => {
    const availableQuestions = questions.filter((_, idx) => !usedQuestions.has(idx));
    
    if (availableQuestions.length === 0) {
      // Reset used questions if we've used them all
      setUsedQuestions(new Set());
      return loadNextQuestion();
    }
    
    const randomQuestion = availableQuestions[Math.floor(Math.random() * availableQuestions.length)];
    const questionIndex = questions.indexOf(randomQuestion);
    
    setCurrentQuestion({ ...randomQuestion, index: questionIndex });
    setFeedback(null);
  };

  const handleAnswer = (selectedOption) => {
    if (!currentQuestion || feedback) return;

    const isCorrect = selectedOption === currentQuestion.answer;
    
    // Update used questions
    const newUsed = new Set(usedQuestions);
    newUsed.add(currentQuestion.index);
    setUsedQuestions(newUsed);

    if (isCorrect) {
      // Correct answer: step BACK (away from death)
      const newStage = Math.max(0, hangmanStage - 1);
      setHangmanStage(newStage);
      setStreak(streak + 1);
      setScore(score + (10 + streak * 2));
      
      const newAnswered = questionsAnswered + 1;
      setQuestionsAnswered(newAnswered);
      
      setFeedback({
        correct: isCorrect,
        selectedOption,
        correctAnswer: currentQuestion.answer,
        explanation: currentQuestion.explanation,
        isWin: newStage === 0,
        isLose: false
      });
    } else {
      // Wrong answer: step FORWARD (toward death)
      const newStage = Math.min(MAX_STAGE, hangmanStage + 1);
      setHangmanStage(newStage);
      setStreak(0);
      
      const newAnswered = questionsAnswered + 1;
      setQuestionsAnswered(newAnswered);
      
      setFeedback({
        correct: isCorrect,
        selectedOption,
        correctAnswer: currentQuestion.answer,
        explanation: currentQuestion.explanation,
        isWin: false,
        isLose: newStage >= MAX_STAGE
      });
    }
  };

  const handleNextQuestion = () => {
    if (feedback?.isWin) {
      setGameState('won');
    } else if (feedback?.isLose) {
      setGameState('lost');
    } else {
      loadNextQuestion();
    }
  };

  const renderHangman = () => {
    return (
      <svg className="hangman-svg" viewBox="0 0 200 280" style={{width: '100%', maxWidth: '300px', height: 'auto'}}>
        {/* Gallows - always visible */}
        <line x1="20" y1="260" x2="180" y2="260" stroke="#654321" strokeWidth="6" strokeLinecap="round" />
        <line x1="60" y1="260" x2="60" y2="20" stroke="#654321" strokeWidth="6" strokeLinecap="round" />
        <line x1="60" y1="20" x2="140" y2="20" stroke="#654321" strokeWidth="6" strokeLinecap="round" />
        <line x1="140" y1="20" x2="140" y2="50" stroke="#654321" strokeWidth="4" strokeLinecap="round" />
        
        {/* Support beam */}
        <line x1="60" y1="40" x2="80" y2="20" stroke="#654321" strokeWidth="3" strokeLinecap="round" />
        
        {/* Stage 7 - Dead (X eyes) */}
        {hangmanStage >= 7 && (
          <>
            {/* Head */}
            <circle cx="140" cy="70" r="20" stroke="#2c3e50" strokeWidth="3" fill="#f8d7da" />
            {/* X eyes */}
            <line x1="133" y1="65" x2="138" y2="70" stroke="#000" strokeWidth="2" />
            <line x1="138" y1="65" x2="133" y2="70" stroke="#000" strokeWidth="2" />
            <line x1="142" y1="65" x2="147" y2="70" stroke="#000" strokeWidth="2" />
            <line x1="147" y1="65" x2="142" y2="70" stroke="#000" strokeWidth="2" />
            {/* Frown */}
            <path d="M 132 78 Q 140 74 148 78" stroke="#000" strokeWidth="2" fill="none" />
            {/* Body */}
            <line x1="140" y1="90" x2="140" y2="160" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            {/* Arms */}
            <line x1="140" y1="110" x2="110" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="110" x2="170" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            {/* Legs */}
            <line x1="140" y1="160" x2="120" y2="200" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="160" x2="160" y2="200" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
        
        {/* Stage 6 - Both legs */}
        {hangmanStage === 6 && (
          <>
            <circle cx="140" cy="70" r="20" stroke="#2c3e50" strokeWidth="3" fill="#ffeaa7" />
            <circle cx="133" cy="67" r="2" fill="#000" />
            <circle cx="147" cy="67" r="2" fill="#000" />
            <path d="M 133 78 Q 140 75 147 78" stroke="#000" strokeWidth="2" fill="none" />
            <line x1="140" y1="90" x2="140" y2="160" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="110" x2="110" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="110" x2="170" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="160" x2="120" y2="200" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="160" x2="160" y2="200" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
        
        {/* Stage 5 - Right leg */}
        {hangmanStage === 5 && (
          <>
            <circle cx="140" cy="70" r="20" stroke="#2c3e50" strokeWidth="3" fill="#ffeaa7" />
            <circle cx="133" cy="67" r="2" fill="#000" />
            <circle cx="147" cy="67" r="2" fill="#000" />
            <line x1="135" y1="77" x2="145" y2="77" stroke="#000" strokeWidth="2" strokeLinecap="round" />
            <line x1="140" y1="90" x2="140" y2="160" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="110" x2="110" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="110" x2="170" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="160" x2="160" y2="200" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
        
        {/* Stage 4 - Both arms */}
        {hangmanStage === 4 && (
          <>
            <circle cx="140" cy="70" r="20" stroke="#2c3e50" strokeWidth="3" fill="#ffeaa7" />
            <circle cx="133" cy="67" r="2" fill="#000" />
            <circle cx="147" cy="67" r="2" fill="#000" />
            <line x1="135" y1="77" x2="145" y2="77" stroke="#000" strokeWidth="2" strokeLinecap="round" />
            <line x1="140" y1="90" x2="140" y2="160" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="110" x2="110" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="110" x2="170" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
        
        {/* Stage 3 - Right arm and body (STARTING POINT) */}
        {hangmanStage === 3 && (
          <>
            <circle cx="140" cy="70" r="20" stroke="#2c3e50" strokeWidth="3" fill="#ffeaa7" />
            <circle cx="133" cy="67" r="2" fill="#000" />
            <circle cx="147" cy="67" r="2" fill="#000" />
            <line x1="135" y1="77" x2="145" y2="77" stroke="#000" strokeWidth="2" strokeLinecap="round" />
            <line x1="140" y1="90" x2="140" y2="160" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
            <line x1="140" y1="110" x2="170" y2="130" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
        
        {/* Stage 2 - Body only */}
        {hangmanStage === 2 && (
          <>
            <circle cx="140" cy="70" r="20" stroke="#2c3e50" strokeWidth="3" fill="#ffeaa7" />
            <circle cx="133" cy="67" r="2" fill="#000" />
            <circle cx="147" cy="67" r="2" fill="#000" />
            <path d="M 135 77 Q 140 80 145 77" stroke="#000" strokeWidth="2" fill="none" />
            <line x1="140" y1="90" x2="140" y2="160" stroke="#2c3e50" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
        
        {/* Stage 1 - Head only */}
        {hangmanStage === 1 && (
          <>
            <circle cx="140" cy="70" r="20" stroke="#2c3e50" strokeWidth="3" fill="#ffeaa7" />
            <circle cx="133" cy="67" r="2" fill="#000" />
            <circle cx="147" cy="67" r="2" fill="#000" />
            <path d="M 135 77 Q 140 80 145 77" stroke="#000" strokeWidth="2" fill="none" />
          </>
        )}
        
        {/* Stage 0 - Safe! (empty noose with smile) */}
        {hangmanStage === 0 && (
          <>
            <ellipse cx="140" cy="55" rx="3" ry="8" stroke="#654321" strokeWidth="2" fill="none" />
            <text x="100" y="150" fontSize="24" fill="#27ae60" fontWeight="bold">SAFE!</text>
          </>
        )}
      </svg>
    );
  };

  const getStageMessage = () => {
    if (hangmanStage === 0) return "🎉 You're SAFE!";
    if (hangmanStage === 1) return "😊 Almost there!";
    if (hangmanStage === 2) return "😌 Getting safer...";
    if (hangmanStage === 3) return "😐 Starting point";
    if (hangmanStage === 4) return "😟 Getting dangerous...";
    if (hangmanStage === 5) return "😰 One more wrong!";
    if (hangmanStage === 6) return "😱 Critical!";
    if (hangmanStage >= 7) return "💀 Game Over";
    return "";
  };

  if (!data || !data.subjects) {
    return <div className="hangman-game"><p>Loading...</p></div>;
  }

  return (
    <div className="hangman-game">
      <div className="hangman-header">
        <h2>🎯 MCQ Hangman</h2>
        <div className="hangman-stats">
          <span>Score: {score}</span>
          <span>Streak: {streak}</span>
          <span>Questions: {questionsAnswered}</span>
        </div>
      </div>

      {gameState === 'menu' && (
        <div className="hangman-menu">
          <h3>Answer questions to escape the gallows!</h3>
          <p className="game-rules">
            ✅ <strong>Correct answer:</strong> Step back (away from death)<br/>
            ❌ <strong>Wrong answer:</strong> Step forward (toward death)<br/>
            🎯 <strong>Goal:</strong> Reach safety (stage 0) before death (stage 7)
          </p>
          
          <div className="subject-filter">
            <label>Subject:</label>
            <select 
              value={selectedSubject} 
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="all">All Subjects</option>
              {data.subjects.map(subject => (
                <option key={subject.name} value={subject.name}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>

          <button 
            className="start-button" 
            onClick={startGame}
            disabled={questions.length === 0}
          >
            {questions.length === 0 ? 'No Questions Available' : 'Start Game'}
          </button>
          
          <p className="question-count">
            {questions.length} questions available
          </p>
        </div>
      )}

      {gameState === 'playing' && currentQuestion && (
        <div className="hangman-playing">
          <div className="hangman-visual">
            {renderHangman()}
            <div className="stage-indicator">
              <div className="stage-message">{getStageMessage()}</div>
              <div className="stage-bar">
                {[...Array(8)].map((_, i) => (
                  <div 
                    key={i} 
                    className={`stage-dot ${i === hangmanStage ? 'active' : ''} ${i === 0 ? 'safe' : i >= 6 ? 'danger' : ''}`}
                  />
                ))}
              </div>
              <div className="stage-labels">
                <span>SAFE</span>
                <span>START</span>
                <span>DEATH</span>
              </div>
            </div>
          </div>

          <div className="question-area">
            <div className="question-header">
              <span className="question-subject">{currentQuestion.subject}</span>
              <span className="question-topic">{currentQuestion.topic}</span>
            </div>
            
            <div className="question-text">
              {currentQuestion.question}
            </div>

            <div className="options-grid">
              {currentQuestion.options.map((option, idx) => (
                <button
                  key={idx}
                  className={`option-button ${
                    feedback 
                      ? option === currentQuestion.answer 
                        ? 'correct' 
                        : option === feedback.selectedOption 
                          ? 'incorrect' 
                          : ''
                      : ''
                  }`}
                  onClick={() => handleAnswer(option)}
                  disabled={feedback !== null}
                >
                  {option}
                </button>
              ))}
            </div>

            {feedback && (
              <div className={`feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
                <div className="feedback-header">
                  {feedback.correct ? '✅ Correct! Stepping back...' : '❌ Wrong! Stepping forward...'}
                </div>
                <div className="feedback-explanation">
                  {feedback.explanation}
                </div>
                <button 
                  className="next-question-button"
                  onClick={handleNextQuestion}
                >
                  {feedback.isWin ? '🎉 See Results' : feedback.isLose ? '💀 See Results' : '➡️ Next Question'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {gameState === 'won' && (
        <div className="hangman-result won">
          <h2>🎉 You Escaped!</h2>
          <div className="result-visual">
            {renderHangman()}
          </div>
          <p>You reached safety!</p>
          <div className="result-stats">
            <div>Final Score: <strong>{score}</strong></div>
            <div>Questions Answered: <strong>{questionsAnswered}</strong></div>
            <div>Best Streak: <strong>{streak}</strong></div>
          </div>
          <button className="play-again-button" onClick={startGame}>
            Play Again
          </button>
          <button className="menu-button" onClick={() => setGameState('menu')}>
            Back to Menu
          </button>
        </div>
      )}

      {gameState === 'lost' && (
        <div className="hangman-result lost">
          <h2>💀 Game Over</h2>
          <div className="result-visual">
            {renderHangman()}
          </div>
          <p>You reached the gallows...</p>
          <div className="result-stats">
            <div>Final Score: <strong>{score}</strong></div>
            <div>Questions Answered: <strong>{questionsAnswered}</strong></div>
          </div>
          <button className="play-again-button" onClick={startGame}>
            Try Again
          </button>
          <button className="menu-button" onClick={() => setGameState('menu')}>
            Back to Menu
          </button>
        </div>
      )}
    </div>
  );
};

export default HangmanGame;
